import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { money } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight, ExternalLink, FileDown } from "lucide-react";

const fmtDelta = (n) => (n > 0 ? `+${money(n)}` : n < 0 ? `−${money(Math.abs(n))}` : money(0));
const deltaClass = (n) => (n > 0 ? "text-rose-600" : n < 0 ? "text-emerald-600" : "text-slate-400");
const MISSING = "no reason given";

function whatOf(r) {
  if (r.in_a === false) return `Added · ${money(r.b)}`;
  if (r.in_b === false) return `Removed · ${money(r.a)}`;
  if (r.changes?.length) return r.changes.map((c) => `${c.field.replace(/_/g, " ")} ${c.from ?? "—"} → ${c.to ?? "—"}`).join("; ");
  if (r.added || r.removed) return [r.added?.length && `added ${r.added.join(", ")}`, r.removed?.length && `removed ${r.removed.join(", ")}`].filter(Boolean).join("; ");
  if (r.field) return `${r.from ?? "—"} → ${r.to ?? "—"}`;
  return "re-priced";
}

function Why({ why }) {
  const missing = !why || why === MISSING;
  return <span className={missing ? "text-rose-600 font-semibold" : "text-slate-600"} data-testid={missing ? "cmp-why-missing" : "cmp-why"}>{missing ? MISSING : why}</span>;
}

function Row({ r, i, onOpen }) {
  const where = r.where.split(" → ").pop();
  return (
    <tr className="border-t border-slate-100 hover:bg-[#003E87]/[0.04] cursor-pointer" data-testid={`cmp-row-${i}`} onClick={() => onOpen(r)} title="Open this spot on that version">
      <td className="px-3 py-1.5 text-slate-800"><span className="inline-flex items-center gap-1">{where}<ExternalLink className="w-3 h-3 text-slate-300" /></span></td>
      <td className="px-3 py-1.5 text-slate-600" data-testid={`cmp-what-${i}`}>{whatOf(r)}</td>
      <td className={`px-3 py-1.5 text-right font-mono ${deltaClass(r.delta || 0)}`}>{r.delta != null ? fmtDelta(r.delta) : "—"}</td>
      <td className="px-3 py-1.5"><Why why={r.why} /></td>
    </tr>
  );
}

// WHAT changed, WHERE, WHY — grouped by where, subtotals footing to the difference in totals; a row opens that spot on that version.
export function VersionCompareDialog({ open, onOpenChange, bidId, versions }) {
  const navigate = useNavigate();
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !versions?.length) return;
    const act = versions.find((v) => v.is_active) || versions[versions.length - 1];
    const before = [...versions].filter((v) => (v.seq ?? 0) < (act.seq ?? 0));
    const prev = before.filter((v) => v.sent_at).pop() || before.pop() || versions[0];
    setA((cur) => cur || prev.version_id); setB((cur) => cur || act.version_id);
  }, [open, versions]);
  useEffect(() => {
    if (!open || !a || !b) return;
    setLoading(true);
    api.get(`/bids/${bidId}/versions/compare`, { params: { a, b } }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [open, a, b, bidId]);
  const openSpot = (r) => { onOpenChange(false); navigate(r.link || `/bids/${bidId}?version=${b}`); };
  const byGroup = {};
  (data?.lines || []).forEach((r) => { const g = r.where.split(" → line")[0]; (byGroup[g] = byGroup[g] || []).push(r); });
  const other = [...(data?.crews || []), ...(data?.terms || [])];
  let n = 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="version-compare-dialog">
        <DialogHeader>
          <DialogTitle>Compare versions — what changed, where, why</DialogTitle>
          <DialogDescription>Rows are grouped by where they changed. Each shows before → after, the dollar effect and the reason. Click a row to open that spot on that version.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 text-sm">
          <select data-testid="cmp-a" value={a} onChange={(e) => setA(e.target.value)} className="border border-slate-200 rounded-md h-9 px-2 flex-1 bg-white">
            {versions.map((v) => <option key={v.version_id} value={v.version_id}>{v.label}{v.sent_at ? ` · sent ${v.sent_at.slice(0, 10)}` : ""}</option>)}
          </select>
          <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
          <select data-testid="cmp-b" value={b} onChange={(e) => setB(e.target.value)} className="border border-slate-200 rounded-md h-9 px-2 flex-1 bg-white">
            {versions.map((v) => <option key={v.version_id} value={v.version_id}>{v.label}{v.sent_at ? ` · sent ${v.sent_at.slice(0, 10)}` : ""}</option>)}
          </select>
          {a && b && <a href={`${API_BASE}/bids/${bidId}/versions/brief?a=${a}&b=${b}`} target="_blank" rel="noreferrer" data-testid="cmp-brief-pdf"
            className="inline-flex items-center gap-1 text-xs text-[#003E87] hover:underline shrink-0"><FileDown className="w-3.5 h-3.5" /> Brief PDF</a>}
        </div>
        {loading && <div className="text-xs text-slate-400 py-6 text-center" data-testid="cmp-loading">Comparing…</div>}
        {data && !loading && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Stat label={data.a.label} value={money(data.a.total)} sub={data.a.sent_at ? `sent ${data.a.sent_at.slice(0, 10)}` : "not sent"} />
              <Stat label={data.b.label} value={money(data.b.total)} sub={data.b.sent_at ? `sent ${data.b.sent_at.slice(0, 10)}` : "not sent"} />
              <div className="rounded-lg border border-slate-200 p-3 text-center" data-testid="cmp-total-delta">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Difference</div>
                <div className={`text-lg font-mono font-semibold ${deltaClass(data.delta)}`}>{fmtDelta(data.delta)}</div>
                <div className="text-[11px] text-rose-600" data-testid="cmp-reasons-missing">{data.reasons_missing ? `${data.reasons_missing} row(s) with no reason given` : "every row has a reason"}</div>
              </div>
            </div>
            <div className="text-xs text-slate-600 rounded-lg bg-slate-50 p-3" data-testid="cmp-why-version">
              {(data.a.note || data.b.note) && <div className="mb-1 text-amber-800" data-testid="cmp-priced-note">{[data.a.note, data.b.note].filter(Boolean).join(" · ")}</div>}
              <b>Why {data.b.label} was opened:</b> <Why why={data.b.why || data.b.request?.text} />
              {data.b.change_order && <div className="mt-1">Change order: base {money(data.b.change_order.base_total)} + CO {fmtDelta(data.b.change_order.co_total)} = contract value <b>{money(data.b.change_order.contract_value)}</b></div>}
            </div>
            <div className="border border-slate-100 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500"><tr>
                  <th className="text-left px-3 py-2 font-medium w-[30%]">Where</th><th className="text-left px-3 py-2 font-medium">What (before → after)</th>
                  <th className="text-right px-3 py-2 font-medium w-24">$ effect</th><th className="text-left px-3 py-2 font-medium w-[26%]">Why</th></tr></thead>
                <tbody>
                  {(data.groups || []).map((g) => (
                    <React.Fragment key={g.where}>
                      <tr className="bg-[#003E87]/[0.06] border-t border-slate-200" data-testid={`cmp-group-${g.where}`}>
                        <td className="px-3 py-1.5 font-semibold text-slate-800" colSpan={2}>{g.where} <span className="text-slate-400 font-normal">· {g.rows} change{g.rows === 1 ? "" : "s"}</span></td>
                        <td className={`px-3 py-1.5 text-right font-mono font-semibold ${deltaClass(g.delta)}`} data-testid="cmp-group-subtotal">{fmtDelta(g.delta)}</td>
                        <td className="px-3 py-1.5 text-slate-400">subtotal</td>
                      </tr>
                      {(byGroup[g.where] || []).map((r) => <Row key={n} r={r} i={n++} onOpen={openSpot} />)}
                    </React.Fragment>
                  ))}
                  {other.length > 0 && <tr className="bg-[#003E87]/[0.06] border-t border-slate-200"><td className="px-3 py-1.5 font-semibold text-slate-800" colSpan={4}>Crews, machines and commercial terms</td></tr>}
                  {other.map((r) => <Row key={n} r={r} i={n++} onOpen={openSpot} />)}
                  {!data.groups?.length && !other.length && <tr><td colSpan={4} className="px-3 py-5 text-center text-slate-400" data-testid="cmp-empty">No line, crew or term differences — the change is in calculators or rates ({fmtDelta(data.delta)}).</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="cmp-foot">
              {[["Lines added", data.effect.lines_added], ["Lines removed", data.effect.lines_removed], ["Lines changed", data.effect.lines_changed], ["Calculators, rates & terms", data.effect.calculators_and_terms], ["Difference in totals", data.effect.total]].map(([l, v]) => (
                <div key={l} className={`rounded-lg border px-2.5 py-2 ${l.startsWith("Difference") ? "border-[#003E87]" : "border-slate-100"}`}>
                  <div className="text-[10px] text-slate-400">{l}</div><div className={`text-xs font-mono ${deltaClass(v)}`}>{fmtDelta(v)}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between text-[11px] text-slate-400">
              <span>{data.legend}</span>
              <Button variant="outline" size="sm" data-testid="cmp-file-brief" onClick={async () => {
                try { const r = await api.post(`/bids/${bidId}/versions/brief`, { a, b }); const { toast } = await import("sonner"); toast.success(`Filed ${r.data.document.name} to Internal`); }
                catch { const { toast } = await import("sonner"); toast.error("Could not file the brief"); }
              }}>File brief to Internal</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3 text-center">
      <div className="text-[10px] uppercase tracking-wide text-slate-400 truncate">{label}</div>
      <div className="text-lg font-mono font-semibold text-slate-800">{value}</div>
      <div className="text-[11px] text-slate-400">{sub}</div>
    </div>
  );
}
